import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { formatIDR, formatDateTime, formatDate } from "../lib/format";
import { periodRange, PERIOD_OPTIONS, buildQuery } from "../lib/period";
import { generateMonthlyReportPDF } from "../lib/reportPdf";
import PdfPreviewDialog from "../components/PdfPreviewDialog";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Download, BookOpen, ScrollText, Scale, LineChart as LineChartIcon, Wallet, FileDown, Lock, Activity, CalendarRange } from "lucide-react";
import * as XLSX from "xlsx";

const exportXLSX = (rows, sheet, filename) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

const TYPE_BADGE = {
  asset: "bg-blue-100 text-blue-800 border-blue-200",
  liability: "bg-orange-100 text-orange-800 border-orange-200",
  equity: "bg-purple-100 text-purple-800 border-purple-200",
  revenue: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expense: "bg-red-100 text-red-800 border-red-200",
};

const fmtPct = (v) => v == null ? "-" : `${(v * 100).toFixed(2)}%`;
const fmtRatio = (v) => v == null ? "-" : v.toFixed(2);

const RatioCard = ({ label, value, hint, tone = "navy" }) => (
  <Card className="card-elevated"><CardContent className="p-4">
    <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`font-display text-2xl font-extrabold mt-1 ${tone === "green" ? "text-emerald-700" : tone === "red" ? "text-red-700" : tone === "gold" ? "text-yellow-700" : "text-navy-900"}`}>{value}</div>
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </CardContent></Card>
);

export default function Reports() {
  const [preset, setPreset] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [periodLabel, setPeriodLabel] = useState("Semua Periode");

  const [company, setCompany] = useState({});
  const [coa, setCoa] = useState([]);
  const [journal, setJournal] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [ledgerAcc, setLedgerAcc] = useState("1000");
  const [tb, setTb] = useState(null);
  const [bs, setBs] = useState(null);
  const [is_, setIs] = useState(null);
  const [cfs, setCfs] = useState(null);
  const [ratios, setRatios] = useState(null);
  const [closings, setClosings] = useState([]);

  const [closeYear, setCloseYear] = useState(new Date().getFullYear() - 1);
  const [closeNote, setCloseNote] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [pdfPrev, setPdfPrev] = useState({ open: false, url: "", filename: "", title: "" });

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const r = periodRange(key);
    setFromDate(r.from); setToDate(r.to); setPeriodLabel(r.label);
  };

  const reload = useCallback(async () => {
    const q = buildQuery(fromDate, toDate);
    const [coaR, jR, tbR, bsR, isR, cfsR, ratR, ledR, closR] = await Promise.all([
      api.get("/accounting/chart-of-accounts"),
      api.get(`/accounting/journal${q}`),
      api.get(`/accounting/trial-balance${toDate ? `?to_date=${toDate}` : ""}`),
      api.get(`/accounting/balance-sheet${toDate ? `?to_date=${toDate}` : ""}`),
      api.get(`/accounting/income-statement${q}`),
      api.get(`/accounting/cash-flow-statement${q}`),
      api.get(`/accounting/financial-ratios${q}`),
      api.get(`/accounting/ledger?account_code=${ledgerAcc}${q ? `&${q.slice(1)}` : ""}`),
      api.get("/accounting/closing-entries"),
    ]);
    setCoa(coaR.data); setJournal(jR.data); setTb(tbR.data); setBs(bsR.data);
    setIs(isR.data); setCfs(cfsR.data); setRatios(ratR.data); setLedger(ledR.data);
    setClosings(closR.data);
  }, [fromDate, toDate, ledgerAcc]);

  useEffect(() => { api.get("/company-profile").then((r) => setCompany(r.data)).catch(() => {}); }, []);
  useEffect(() => { reload(); }, [reload]);

  const changeLedger = (code) => setLedgerAcc(code);

  const printMonthly = async () => {
    if (!bs || !is_ || !cfs) return toast.error("Data belum siap");
    setPrinting(true);
    try {
      const res = generateMonthlyReportPDF({ company, period: periodLabel, balanceSheet: bs, incomeStatement: is_, cashFlow: cfs, trialBalance: tb, ratios, preview: true });
      if (res?.url) setPdfPrev({ open: true, url: res.url, filename: res.filename, title: `Preview Laporan ${periodLabel}` });
    } catch (e) { toast.error("Gagal cetak PDF: " + e.message); }
    finally { setPrinting(false); }
  };

  const submitClosing = async () => {
    if (!closeYear) return toast.error("Tahun wajib diisi");
    if (!window.confirm(`Tutup buku tahun ${closeYear}? Aksi ini akan membuat jurnal penutup permanen.`)) return;
    try {
      await api.post("/accounting/closing-entry", { year: Number(closeYear), note: closeNote });
      toast.success(`Buku tahun ${closeYear} berhasil ditutup`);
      setCloseOpen(false); setCloseNote("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal tutup buku"); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Akuntansi</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Laporan Keuangan Lengkap</h1>
          <p className="text-sm text-slate-500 mt-1">Semua laporan auto-generate dari jurnal double-entry. Pilih periode untuk membandingkan performa.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="closing-open-btn"><Lock size={14} className="mr-1" />Tutup Buku</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Jurnal Penutup Tahun</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 text-xs text-yellow-800">
                  ⚠️ Aksi ini akan menghasilkan jurnal permanen yang mentransfer semua saldo Pendapatan & Beban tahun tersebut ke akun <strong>Laba Ditahan (3100)</strong>.
                </div>
                <div><Label>Tahun Buku</Label><Input type="number" value={closeYear} onChange={(e) => setCloseYear(e.target.value)} data-testid="closing-year-input" /></div>
                <div><Label>Catatan (opsional)</Label><Textarea value={closeNote} onChange={(e) => setCloseNote(e.target.value)} placeholder="Ditutup oleh akuntan setelah audit..." /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCloseOpen(false)}>Batal</Button>
                <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={submitClosing} data-testid="closing-submit-btn">Konfirmasi Tutup Buku</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={printMonthly} disabled={printing} data-testid="print-monthly-btn">
            <FileDown size={14} className="mr-1" />{printing ? "Mencetak..." : "Cetak PDF Bulanan"}
          </Button>
        </div>
      </div>

      {/* Period Filter Bar */}
      <Card className="card-elevated bg-navy-900 text-white border-0">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-shrink-0"><CalendarRange className="text-yellow-400 mt-6" size={20} /></div>
          <div className="w-56">
            <Label className="text-white/70">Periode</Label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="period-preset-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIOD_OPTIONS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Label className="text-white/70">Dari</Label>
            <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPreset("custom"); setPeriodLabel("Custom"); }} className="bg-white/10 border-white/20 text-white" data-testid="period-from-input" />
          </div>
          <div className="w-44">
            <Label className="text-white/70">Sampai</Label>
            <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPreset("custom"); setPeriodLabel("Custom"); }} className="bg-white/10 border-white/20 text-white" data-testid="period-to-input" />
          </div>
          <div className="flex-1 min-w-[180px] text-right">
            <div className="text-[10px] uppercase tracking-widest text-yellow-400">Periode Aktif</div>
            <div className="font-display font-bold text-xl">{periodLabel}</div>
            {fromDate && toDate && <div className="text-[11px] text-white/60 font-mono">{fromDate} → {toDate}</div>}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="coa">
        <TabsList data-testid="reports-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="coa" data-testid="tab-coa"><BookOpen size={14} className="mr-1" />Bagan Akun</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal"><ScrollText size={14} className="mr-1" />Jurnal</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Buku Besar</TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-tb"><Scale size={14} className="mr-1" />Neraca Percobaan</TabsTrigger>
          <TabsTrigger value="bs" data-testid="tab-bs">Neraca</TabsTrigger>
          <TabsTrigger value="is" data-testid="tab-is"><LineChartIcon size={14} className="mr-1" />Laba-Rugi</TabsTrigger>
          <TabsTrigger value="cfs" data-testid="tab-cfs"><Wallet size={14} className="mr-1" />Arus Kas</TabsTrigger>
          <TabsTrigger value="ratios" data-testid="tab-ratios"><Activity size={14} className="mr-1" />Rasio</TabsTrigger>
          <TabsTrigger value="closing" data-testid="tab-closing"><Lock size={14} className="mr-1" />Tutup Buku</TabsTrigger>
        </TabsList>

        {/* Chart of Accounts */}
        <TabsContent value="coa">
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm" onClick={() => exportXLSX(coa, "CoA", "Bagan_Akun")} data-testid="export-coa-btn"><Download size={14} className="mr-1" />Excel</Button>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama Akun</TableHead><TableHead>Tipe</TableHead><TableHead>Saldo Normal</TableHead></TableRow></TableHeader>
              <TableBody>
                {coa.map((a) => (
                  <TableRow key={a.code}>
                    <TableCell className="font-mono font-semibold">{a.code}</TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell><Badge className={TYPE_BADGE[a.type]}>{a.type.toUpperCase()}</Badge></TableCell>
                    <TableCell className="text-sm capitalize">{a.normal}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* Journal */}
        <TabsContent value="journal">
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex justify-between mb-3">
              <div className="font-display font-bold text-slate-800">Jurnal Umum · {journal.length} entri</div>
              <Button variant="outline" size="sm" onClick={() => {
                const rows = [];
                journal.forEach((j) => j.lines.forEach((l) => rows.push({
                  "Tanggal": formatDateTime(j.date), "Ref": j.ref_number, "Deskripsi": j.description,
                  "Kode Akun": l.account_code, "Nama Akun": l.account_name,
                  "Debit": l.debit || "", "Kredit": l.credit || "",
                })));
                exportXLSX(rows, "Jurnal", "Jurnal_Umum");
              }} data-testid="export-journal-btn"><Download size={14} className="mr-1" />Excel</Button>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              {journal.map((j) => (
                <div key={j.id} className="rounded-lg border border-slate-200 p-3 bg-white" data-testid={`journal-${j.ref_number}`}>
                  <div className="flex justify-between text-xs text-slate-500 mb-2">
                    <span>{formatDateTime(j.date)} · <span className="font-mono">{j.ref_number}</span></span>
                    <span className={`uppercase tracking-widest ${j.ref_type === "CLOSING" ? "text-purple-700 font-bold" : ""}`}>{j.ref_type}</span>
                  </div>
                  <div className="text-sm font-medium text-slate-700 mb-2">{j.description}</div>
                  <Table>
                    <TableBody>
                      {j.lines.map((l, i) => (
                        <TableRow key={i} className="border-0">
                          <TableCell className="py-1 font-mono text-xs w-20">{l.account_code}</TableCell>
                          <TableCell className={`py-1 text-sm ${l.debit > 0 ? "" : "pl-8 text-slate-600"}`}>{l.account_name}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-sm w-32">{l.debit > 0 ? formatIDR(l.debit) : ""}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-sm w-32">{l.credit > 0 ? formatIDR(l.credit) : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
              {journal.length === 0 && <div className="text-center text-slate-400 py-8">Belum ada jurnal di periode ini.</div>}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* Ledger */}
        <TabsContent value="ledger">
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <div className="w-72">
                <Label>Pilih Akun</Label>
                <Select value={ledgerAcc} onValueChange={changeLedger}>
                  <SelectTrigger data-testid="ledger-account-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{coa.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={() => ledger && exportXLSX(ledger.rows.map((r) => ({
                "Tanggal": formatDateTime(r.date), "Ref": r.ref_number, "Deskripsi": r.description,
                "Debit": r.debit, "Kredit": r.credit, "Saldo": r.balance,
              })), "Ledger", `Ledger_${ledgerAcc}`)}><Download size={14} className="mr-1" />Excel</Button>
            </div>
            {ledger && (
              <>
                <div className="mb-4 rounded-lg bg-navy-900 text-white p-4">
                  <div className="text-[11px] uppercase tracking-widest text-yellow-400">{ledger.account.type.toUpperCase()}</div>
                  <div className="font-display text-lg font-bold">{ledger.account.code} — {ledger.account.name}</div>
                  <div className="mt-2 text-xs text-white/70">Saldo Akhir</div>
                  <div className="font-display text-2xl font-extrabold text-yellow-400">{formatIDR(ledger.ending_balance)}</div>
                </div>
                <Table data-testid="ledger-table">
                  <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Ref</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {ledger.rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">{formatDateTime(r.date)}</TableCell>
                        <TableCell className="font-mono text-xs">{r.ref_number}</TableCell>
                        <TableCell className="text-sm">{r.description}</TableCell>
                        <TableCell className="text-right font-mono">{r.debit > 0 ? formatIDR(r.debit) : ""}</TableCell>
                        <TableCell className="text-right font-mono">{r.credit > 0 ? formatIDR(r.credit) : ""}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{formatIDR(r.balance)}</TableCell>
                      </TableRow>
                    ))}
                    {ledger.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Belum ada mutasi</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Trial Balance */}
        <TabsContent value="tb">
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex justify-between mb-3">
              <div className="font-display font-bold text-slate-800">Neraca Percobaan {toDate && <span className="text-xs text-slate-500 font-normal">per {formatDate(toDate)}</span>}</div>
              <Button variant="outline" size="sm" onClick={() => tb && exportXLSX(tb.rows.map((r) => ({
                "Kode": r.code, "Akun": r.name, "Tipe": r.type,
                "Debit": r.debit, "Kredit": r.credit,
              })), "TrialBalance", "Neraca_Percobaan")} data-testid="export-tb-btn"><Download size={14} className="mr-1" />Excel</Button>
            </div>
            {tb && (
              <Table data-testid="tb-table">
                <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Akun</TableHead><TableHead>Tipe</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {tb.rows.map((r) => (
                    <TableRow key={r.code}>
                      <TableCell className="font-mono text-sm">{r.code}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell><Badge className={TYPE_BADGE[r.type]}>{r.type}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{r.debit > 0 ? formatIDR(r.debit) : "-"}</TableCell>
                      <TableCell className="text-right font-mono">{r.credit > 0 ? formatIDR(r.credit) : "-"}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-display font-bold">
                    <TableCell colSpan={3} className="text-right">TOTAL</TableCell>
                    <TableCell className="text-right font-mono">{formatIDR(tb.total_debit)}</TableCell>
                    <TableCell className="text-right font-mono">{formatIDR(tb.total_credit)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Balance Sheet */}
        <TabsContent value="bs">
          {bs && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <div className="font-display text-xl font-bold text-slate-900">Neraca {toDate && <span className="text-sm text-slate-500 font-normal">per {formatDate(toDate)}</span>}</div>
                <Badge className={bs.balanced ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}>
                  {bs.balanced ? "SEIMBANG ✓" : "TIDAK SEIMBANG"}
                </Badge>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="card-elevated"><CardContent className="p-4">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Aset</div>
                  <Table data-testid="bs-assets">
                    <TableBody>
                      {bs.assets.map((a) => (
                        <TableRow key={a.code}>
                          <TableCell className="font-mono text-xs w-16">{a.code}</TableCell>
                          <TableCell>{a.name}</TableCell>
                          <TableCell className="text-right font-mono">{formatIDR(a.balance)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-navy-900 hover:bg-navy-900">
                        <TableCell colSpan={2} className="font-display font-bold text-white">Total Aset</TableCell>
                        <TableCell className="text-right font-mono font-bold text-yellow-400">{formatIDR(bs.total_assets)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent></Card>
                <div className="space-y-4">
                  <Card className="card-elevated"><CardContent className="p-4">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Liabilitas</div>
                    <Table data-testid="bs-liabilities">
                      <TableBody>
                        {bs.liabilities.map((a) => (
                          <TableRow key={a.code}>
                            <TableCell className="font-mono text-xs w-16">{a.code}</TableCell>
                            <TableCell>{a.name}</TableCell>
                            <TableCell className="text-right font-mono">{formatIDR(a.balance)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-100"><TableCell colSpan={2} className="font-display font-bold">Total Liabilitas</TableCell><TableCell className="text-right font-mono font-bold">{formatIDR(bs.total_liabilities)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </CardContent></Card>
                  <Card className="card-elevated"><CardContent className="p-4">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Ekuitas</div>
                    <Table data-testid="bs-equity">
                      <TableBody>
                        {bs.equity.map((a) => (
                          <TableRow key={a.code}>
                            <TableCell className="font-mono text-xs w-16">{a.code}</TableCell>
                            <TableCell>{a.name}</TableCell>
                            <TableCell className="text-right font-mono">{formatIDR(a.balance)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-100"><TableCell colSpan={2} className="font-display font-bold">Total Ekuitas</TableCell><TableCell className="text-right font-mono font-bold">{formatIDR(bs.total_equity)}</TableCell></TableRow>
                        <TableRow className="bg-navy-900 hover:bg-navy-900"><TableCell colSpan={2} className="font-display font-bold text-white">Liabilitas + Ekuitas</TableCell><TableCell className="text-right font-mono font-bold text-yellow-400">{formatIDR(bs.total_liabilities + bs.total_equity)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </CardContent></Card>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Income Statement */}
        <TabsContent value="is">
          {is_ && (
            <Card className="card-elevated"><CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="font-display text-xl font-bold text-slate-900">Laporan Laba-Rugi <span className="text-sm text-slate-500 font-normal">· {periodLabel}</span></div>
                <Button variant="outline" size="sm" onClick={() => exportXLSX([
                  ...is_.revenue_lines.map((r) => ({ "Kategori": "Pendapatan", "Kode": r.code, "Akun": r.name, "Jumlah": r.amount })),
                  ...is_.expense_lines.map((r) => ({ "Kategori": "Beban", "Kode": r.code, "Akun": r.name, "Jumlah": r.amount })),
                ], "LabaRugi", "Income_Statement")} data-testid="export-is-btn"><Download size={14} className="mr-1" />Excel</Button>
              </div>
              <Table>
                <TableBody>
                  <TableRow className="bg-slate-100"><TableCell colSpan={2} className="font-display font-bold text-slate-800 uppercase text-xs tracking-widest">Pendapatan</TableCell></TableRow>
                  {is_.revenue_lines.map((r) => (
                    <TableRow key={r.code}><TableCell className="pl-6">{r.name}</TableCell><TableCell className="text-right font-mono">{formatIDR(r.amount)}</TableCell></TableRow>
                  ))}
                  <TableRow><TableCell className="font-semibold">Total Pendapatan</TableCell><TableCell className="text-right font-mono font-bold text-emerald-700">{formatIDR(is_.total_revenue)}</TableCell></TableRow>
                  <TableRow><TableCell className="pl-6">Harga Pokok Penjualan (HPP)</TableCell><TableCell className="text-right font-mono">({formatIDR(is_.cogs)})</TableCell></TableRow>
                  <TableRow className="bg-navy-900 hover:bg-navy-900"><TableCell className="font-display font-bold text-white">LABA KOTOR</TableCell><TableCell className="text-right font-mono font-bold text-yellow-400">{formatIDR(is_.gross_profit)}</TableCell></TableRow>
                  <TableRow className="bg-slate-100"><TableCell colSpan={2} className="font-display font-bold text-slate-800 uppercase text-xs tracking-widest">Beban Operasional</TableCell></TableRow>
                  {is_.expense_lines.filter((e) => e.code !== "5000").map((r) => (
                    <TableRow key={r.code}><TableCell className="pl-6">{r.name}</TableCell><TableCell className="text-right font-mono">({formatIDR(r.amount)})</TableCell></TableRow>
                  ))}
                  <TableRow><TableCell className="font-semibold">Total Beban Operasional</TableCell><TableCell className="text-right font-mono">({formatIDR(is_.opex)})</TableCell></TableRow>
                  <TableRow className="bg-yellow-50">
                    <TableCell className="font-display text-lg font-extrabold text-navy-900">LABA BERSIH</TableCell>
                    <TableCell className={`text-right font-mono text-lg font-extrabold ${is_.net_income >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatIDR(is_.net_income)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* Cash Flow */}
        <TabsContent value="cfs">
          {cfs && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <Card className="card-elevated"><CardContent className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total Arus Masuk</div><div className="font-display text-xl font-extrabold mt-1 text-emerald-700">{formatIDR(cfs.operating_in)}</div></CardContent></Card>
                <Card className="card-elevated"><CardContent className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total Arus Keluar</div><div className="font-display text-xl font-extrabold mt-1 text-red-700">{formatIDR(cfs.operating_out)}</div></CardContent></Card>
                <Card className="card-elevated"><CardContent className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Arus Kas Bersih</div><div className={`font-display text-xl font-extrabold mt-1 ${cfs.net_cash >= 0 ? "text-navy-900" : "text-red-700"}`}>{formatIDR(cfs.net_cash)}</div></CardContent></Card>
              </div>
              <Card className="card-elevated"><CardContent className="p-4">
                <Table data-testid="cfs-table">
                  <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Referensi</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">Masuk</TableHead><TableHead className="text-right">Keluar</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {cfs.entries.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">{formatDate(e.date)}</TableCell>
                        <TableCell className="font-mono text-xs">{e.ref_number}</TableCell>
                        <TableCell className="text-sm">{e.description}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-700">{e.inflow > 0 ? formatIDR(e.inflow) : ""}</TableCell>
                        <TableCell className="text-right font-mono text-red-700">{e.outflow > 0 ? formatIDR(e.outflow) : ""}</TableCell>
                      </TableRow>
                    ))}
                    {cfs.entries.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Belum ada pergerakan kas</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent></Card>
            </>
          )}
        </TabsContent>

        {/* Financial Ratios */}
        <TabsContent value="ratios">
          {ratios && (
            <>
              <div className="mb-3">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Kesehatan Bisnis · {periodLabel}</div>
                <h3 className="font-display text-xl font-bold text-slate-900">Rasio Keuangan</h3>
              </div>
              <div className="space-y-4" data-testid="ratios-content">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Likuiditas</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <RatioCard label="Current Ratio" value={fmtRatio(ratios.liquidity.current_ratio)} hint="Aset Lancar ÷ Utang Lancar · sehat ≥ 1.5" tone={ratios.liquidity.current_ratio >= 1.5 ? "green" : ratios.liquidity.current_ratio >= 1 ? "gold" : "red"} />
                    <RatioCard label="Quick Ratio" value={fmtRatio(ratios.liquidity.quick_ratio)} hint="(Kas + Piutang) ÷ Utang Lancar · sehat ≥ 1" tone={ratios.liquidity.quick_ratio >= 1 ? "green" : "gold"} />
                    <RatioCard label="Working Capital" value={formatIDR(ratios.liquidity.working_capital)} hint="Modal kerja tersedia" tone={ratios.liquidity.working_capital >= 0 ? "green" : "red"} />
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Profitabilitas</div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <RatioCard label="Gross Margin" value={fmtPct(ratios.profitability.gross_margin)} hint="Laba Kotor ÷ Pendapatan" tone="gold" />
                    <RatioCard label="Operating Margin" value={fmtPct(ratios.profitability.operating_margin)} hint="EBIT ÷ Pendapatan" tone="gold" />
                    <RatioCard label="Net Margin" value={fmtPct(ratios.profitability.net_margin)} hint="Laba Bersih ÷ Pendapatan" tone={ratios.profitability.net_margin > 0 ? "green" : "red"} />
                    <RatioCard label="ROA" value={fmtPct(ratios.profitability.roa)} hint="Return on Assets" tone="green" />
                    <RatioCard label="ROE" value={fmtPct(ratios.profitability.roe)} hint="Return on Equity" tone="green" />
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Leverage & Efisiensi</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <RatioCard label="Debt to Equity" value={fmtRatio(ratios.leverage.debt_to_equity)} hint="Utang ÷ Ekuitas · < 2 sehat" tone={ratios.leverage.debt_to_equity < 2 ? "green" : "red"} />
                    <RatioCard label="Debt to Assets" value={fmtPct(ratios.leverage.debt_to_assets)} hint="Utang ÷ Aset" />
                    <RatioCard label="Inventory Turnover" value={fmtRatio(ratios.efficiency.inventory_turnover)} hint="HPP ÷ Persediaan · makin tinggi makin efisien" />
                    <RatioCard label="Receivables Turnover" value={fmtRatio(ratios.efficiency.receivables_turnover)} hint="Pendapatan ÷ Piutang" />
                  </div>
                </div>
                <Card className="card-elevated"><CardContent className="p-4">
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Ringkasan Angka</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div><div className="text-xs text-slate-500">Aset Lancar</div><div className="font-mono font-semibold">{formatIDR(ratios.balances.current_assets)}</div></div>
                    <div><div className="text-xs text-slate-500">Utang Lancar</div><div className="font-mono font-semibold">{formatIDR(ratios.balances.current_liabilities)}</div></div>
                    <div><div className="text-xs text-slate-500">Total Aset</div><div className="font-mono font-semibold">{formatIDR(ratios.balances.total_assets)}</div></div>
                    <div><div className="text-xs text-slate-500">Total Ekuitas</div><div className="font-mono font-semibold">{formatIDR(ratios.balances.total_equity)}</div></div>
                    <div><div className="text-xs text-slate-500">Pendapatan</div><div className="font-mono font-semibold text-emerald-700">{formatIDR(ratios.balances.revenue)}</div></div>
                    <div><div className="text-xs text-slate-500">HPP</div><div className="font-mono font-semibold">{formatIDR(ratios.balances.cogs)}</div></div>
                    <div><div className="text-xs text-slate-500">Beban Operasional</div><div className="font-mono font-semibold">{formatIDR(ratios.balances.opex)}</div></div>
                    <div><div className="text-xs text-slate-500">Laba Bersih</div><div className={`font-mono font-semibold ${ratios.balances.net_income >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatIDR(ratios.balances.net_income)}</div></div>
                  </div>
                </CardContent></Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Closing Entries */}
        <TabsContent value="closing">
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display font-bold text-slate-800">Riwayat Tutup Buku</div>
                <p className="text-xs text-slate-500 mt-1">Jurnal penutup tahunan mentransfer saldo Pendapatan & Beban ke akun Laba Ditahan (3100).</p>
              </div>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={() => setCloseOpen(true)} data-testid="closing-open-btn-tab"><Lock size={14} className="mr-1" />Tutup Tahun Baru</Button>
            </div>
            <Table data-testid="closings-table">
              <TableHeader><TableRow><TableHead>Tahun</TableHead><TableHead>Ditutup Pada</TableHead><TableHead className="text-right">Pendapatan</TableHead><TableHead className="text-right">Beban</TableHead><TableHead className="text-right">Laba Bersih</TableHead><TableHead>Referensi</TableHead><TableHead>Catatan</TableHead></TableRow></TableHeader>
              <TableBody>
                {closings.map((c) => (
                  <TableRow key={c.id} data-testid={`closing-row-${c.year}`}>
                    <TableCell className="font-display font-bold text-lg">{c.year}</TableCell>
                    <TableCell className="text-sm">{formatDateTime(c.closed_at)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{formatIDR(c.total_revenue)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{formatIDR(c.total_expense)}</TableCell>
                    <TableCell className={`text-right font-mono font-bold ${c.net_income >= 0 ? "text-navy-900" : "text-red-700"}`}>{formatIDR(c.net_income)}</TableCell>
                    <TableCell className="font-mono text-xs">{c.journal_ref}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs truncate">{c.note || "-"}</TableCell>
                  </TableRow>
                ))}
                {closings.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada tahun yang ditutup</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
      <PdfPreviewDialog open={pdfPrev.open} url={pdfPrev.url} filename={pdfPrev.filename} title={pdfPrev.title} onClose={() => setPdfPrev({ open: false, url: "", filename: "", title: "" })} />
    </div>
  );
}
